import { useEffect, useMemo, useState } from "react";
import { DataRow, DataTable, ProfileTabs, Skeleton } from "@breakingthelines/design-system";

import { handleTabArrowKeyDown } from "@/components/common/tab-keyboard-nav";
import {
  getPlatformPayoutSummary,
  getRevenueSummary,
  type PlatformPayoutStatus,
  type PlatformPayoutSummary,
  type RevenueBillingContext,
  type RevenueMRR,
  type RevenuePeriod,
  type RevenueSummary,
} from "@/lib/admin-api";
import { formatMinorUnits } from "@/lib/money";

import styles from "./revenue-overview.module.css";

// Phase 1 of the Revenue panel. Every figure here comes from
// subscription-service's own memberships ledger, never the Stripe API.
//
// Two rules the layout exists to hold, both of which are wrong the moment
// anything on this page adds numbers together:
//
//   1. PLATFORM and PRINCIPAL are different money. BTL keeps all of a
//      subscription to BTL and only its platform fee on a subscription to a
//      creator. One combined total would overstate BTL's revenue by the
//      creators' share.
//   2. Currencies are never merged. The platform catalogue is priced in gbp,
//      usd and eur, and there is no exchange rate anywhere in this system.
//
// admin-service already returns MRR pre-grouped on both axes, so this page
// renders one figure per group because that is the only shape it is given.
//
// The Payouts section below is phase 2 and is the other kind of truth: what
// Stripe has actually settled. It fetches separately and degrades separately
// (see PayoutsSection), so Stripe being down leaves the ledger figures above
// fully readable.

const PERIOD_TABS = ["Last 30 days", "This month"] as const;

const PERIOD_BY_TAB: Record<(typeof PERIOD_TABS)[number], RevenuePeriod> = {
  "Last 30 days": "last_30d",
  "This month": "this_month",
};

const BILLING_CONTEXT_LABEL: Record<RevenueBillingContext, string> = {
  PLATFORM: "BTL subscriptions",
  PRINCIPAL: "Creator and squad subscriptions",
  UNKNOWN: "Unclassified",
};

// Ordering, not sorting: PLATFORM leads because it is BTL's own revenue.
const BILLING_CONTEXT_ORDER: RevenueBillingContext[] = ["PLATFORM", "PRINCIPAL", "UNKNOWN"];

function formatCount(value: number): string {
  return new Intl.NumberFormat("en-GB").format(value);
}

// UTC, matching the window admin-service computed. Rendering in the browser's
// zone would put two operators on different days.
function formatWindow(start?: string, end?: string): string {
  if (!start || !end) return "";
  const format = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
  };
  const from = format(start);
  const to = format(end);
  if (!from || !to) return "";
  return `${from} to ${to} UTC`;
}

// --- Payouts (Stripe settlement) ---------------------------------------

// Rendered as its own class rather than folded into a colour scale. PAID is
// the settled-good state; FAILED and CANCELED are the two an operator needs
// to notice. UNKNOWN is deliberately styled as neither: Stripe reported a
// state this app does not map, and guessing which of the five it resembles
// is exactly the mistake worth avoiding on a money surface.
const PAYOUT_STATUS_LABEL: Record<PlatformPayoutStatus, string> = {
  PAID: "Paid",
  FAILED: "Failed",
  CANCELED: "Canceled",
  PENDING: "Pending",
  IN_TRANSIT: "In transit",
  UNKNOWN: "Unknown",
};

const PAYOUT_STATUS_CLASS: Record<PlatformPayoutStatus, string> = {
  PAID: styles.statusPaid,
  FAILED: styles.statusFailed,
  CANCELED: styles.statusFailed,
  PENDING: "",
  IN_TRANSIT: "",
  UNKNOWN: "",
};

// What a date cell shows when Stripe did not report one. A plain hyphen, not
// a dash character: this is a table placeholder, not punctuation.
const NOT_REPORTED = "-";

// Date only. A payout arrives on a day; the hour Stripe recorded it is noise
// on this surface. UTC for the same reason the window label is.
function formatSettlementDate(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

// The Payouts section owns its own fetch, its own loading state and its own
// error line.
//
// That separation is the whole point of it being a component. Stripe is a
// third party on a different failure schedule from our own database, and the
// MRR figures above come from subscription-service's memberships table. One
// combined fetch would mean a Stripe outage blanked the subscription ledger
// too, which is both wrong and the more common failure.
function PayoutsSection() {
  const [summary, setSummary] = useState<PlatformPayoutSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getPlatformPayoutSummary()
      .then((response) => {
        if (cancelled) return;
        setSummary(response);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        // Cleared rather than left on screen. A stale balance is a claim
        // about money BTL holds right now, and it stops being true the
        // moment the fetch that would have refreshed it failed.
        setSummary(null);
        setError(err.message || "Stripe settlement figures are unavailable.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const balances = summary?.balances ?? [];
  const payouts = summary?.recentPayouts ?? [];

  return (
    <section className={styles.section} aria-labelledby="revenue-payouts">
      <h2 className={styles.sectionTitle} id="revenue-payouts">
        Stripe payouts
      </h2>
      <p className={styles.derivation}>
        Stripe settlement, not the subscription ledger above. Refunds, disputes, Stripe fees and the
        payout hold period sit between the two, so the figures will not match. BTL&apos;s own Stripe
        account only: creators and squads are paid out on their own connected accounts.
      </p>

      {loading ? (
        <div
          className={styles.loading}
          role="status"
          aria-live="polite"
          aria-label="Loading Stripe payouts..."
        >
          <div className={styles.figures}>
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        </div>
      ) : null}

      {!loading && error ? <p className={styles.unavailable}>{error}</p> : null}

      {!loading && !error && summary ? (
        <>
          {balances.length === 0 ? (
            <p className={styles.empty}>Stripe is holding no funds.</p>
          ) : (
            <div className={styles.groups}>
              {balances.map((balance) => (
                <div className={styles.group} key={balance.currency}>
                  <p className={styles.balanceCurrency}>{balance.currency.toUpperCase()}</p>
                  <div className={styles.figures}>
                    <div className={styles.figure}>
                      <p className={styles.figureValue}>
                        {formatMinorUnits(balance.availableMinorUnits, balance.currency)}
                      </p>
                      <p className={styles.balanceLabel}>Available</p>
                    </div>
                    <div className={styles.figure}>
                      <p className={styles.figureValueMuted}>
                        {formatMinorUnits(balance.pendingMinorUnits, balance.currency)}
                      </p>
                      <p className={styles.balanceLabel}>Pending</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <p className={styles.note}>
            Available is settled and free for the next payout. Pending is still inside Stripe&apos;s
            hold period. They are separate balances and are not added together. Currencies are not
            converted.
          </p>

          {payouts.length === 0 ? (
            <p className={styles.empty}>No payouts yet.</p>
          ) : (
            <DataTable
              columns="minmax(180px, 1.3fr) minmax(120px, 0.9fr) minmax(90px, 0.6fr) minmax(110px, 0.7fr) minmax(120px, 0.8fr) minmax(120px, 0.8fr)"
              header={["Payout", "Amount", "Currency", "Status", "Arrives", "Created"]}
              headerClassName={styles.tableHeader}
            >
              {payouts.map((payout) => (
                <DataRow key={payout.id}>
                  <span className={styles.meta} data-label="Payout">
                    {payout.id}
                  </span>
                  <span className={styles.strong} data-label="Amount">
                    {formatMinorUnits(payout.amountMinorUnits, payout.currency)}
                  </span>
                  <span className={styles.meta} data-label="Currency">
                    {payout.currency.toUpperCase()}
                  </span>
                  <span
                    className={`${styles.status} ${PAYOUT_STATUS_CLASS[payout.status] ?? ""}`}
                    data-label="Status"
                  >
                    {PAYOUT_STATUS_LABEL[payout.status] ?? payout.status}
                  </span>
                  <span className={styles.meta} data-label="Arrives">
                    {formatSettlementDate(payout.arrivalDate) || NOT_REPORTED}
                  </span>
                  <span className={styles.meta} data-label="Created">
                    {formatSettlementDate(payout.createdAt) || NOT_REPORTED}
                  </span>
                </DataRow>
              ))}
            </DataTable>
          )}

          <p className={styles.note}>
            The 20 most recent payouts from BTL&apos;s Stripe account to BTL&apos;s bank. Stripe
            holds the full history.
          </p>
        </>
      ) : null}
    </section>
  );
}

function contextOrder(context: RevenueBillingContext): number {
  const index = BILLING_CONTEXT_ORDER.indexOf(context);
  return index === -1 ? BILLING_CONTEXT_ORDER.length : index;
}

function groupMRR(
  mrr: RevenueMRR[],
): Array<{ context: RevenueBillingContext; rows: RevenueMRR[] }> {
  const groups = new Map<RevenueBillingContext, RevenueMRR[]>();
  for (const row of mrr) {
    const existing = groups.get(row.billingContext);
    if (existing) existing.push(row);
    else groups.set(row.billingContext, [row]);
  }
  return [...groups.entries()]
    .map(([context, rows]) => ({ context, rows }))
    .sort((a, b) => contextOrder(a.context) - contextOrder(b.context));
}

export function RevenueOverviewPage() {
  const [tab, setTab] = useState<(typeof PERIOD_TABS)[number]>(PERIOD_TABS[0]);
  const [summary, setSummary] = useState<RevenueSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const period = PERIOD_BY_TAB[tab];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getRevenueSummary({ period })
      .then((response) => {
        if (cancelled) return;
        setSummary(response);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        // The previous period's figures are cleared rather than left on
        // screen under a new period label. A stale number presented as a
        // current one is worse than no number.
        setSummary(null);
        setError(err.message || "Revenue figures are unavailable.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [period]);

  const mrrGroups = useMemo(() => groupMRR(summary?.mrr ?? []), [summary]);
  const lifecycle = summary?.lifecycle ?? [];
  const tiers = summary?.tiers ?? [];
  const principals = summary?.principals ?? [];
  const windowLabel = formatWindow(summary?.windowStart, summary?.windowEnd);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Revenue</h1>
        <p className={styles.derivation}>
          MRR: active and trialing subscriptions times their billed amount, annual divided by 12.
          Subscription ledger only, not Stripe settlement.
        </p>
      </div>

      <div className={styles.toolbar}>
        <ProfileTabs
          role="tablist"
          aria-label="Reporting period"
          tabs={PERIOD_TABS.map((item) => ({ id: item, label: item }))}
          activeTab={tab}
          onKeyDown={handleTabArrowKeyDown}
          onTabChange={(next) => setTab(next as (typeof PERIOD_TABS)[number])}
        />
        {windowLabel ? <span className={styles.window}>{windowLabel}</span> : null}
      </div>

      {loading ? (
        // The figures below are swapped out entirely while they load, so this
        // holds their shape rather than collapsing the page to a spinner.
        // `role="status"` keeps the announcement, under the same label.
        <div
          className={styles.loading}
          role="status"
          aria-live="polite"
          aria-label="Loading revenue..."
        >
          <Skeleton className="h-6 w-48" />
          <div className={styles.figures}>
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        </div>
      ) : null}

      {!loading && error ? <p className={styles.unavailable}>{error}</p> : null}

      {!loading && !error && summary ? (
        <>
          <section className={styles.section} aria-labelledby="revenue-mrr">
            <h2 className={styles.sectionTitle} id="revenue-mrr">
              Monthly recurring revenue
            </h2>
            {mrrGroups.length === 0 ? (
              <p className={styles.empty}>No active subscriptions.</p>
            ) : (
              <div className={styles.groups}>
                {mrrGroups.map((group) => (
                  <div className={styles.group} key={group.context}>
                    <p className={styles.groupLabel}>{BILLING_CONTEXT_LABEL[group.context]}</p>
                    <div className={styles.figures}>
                      {group.rows.map((row) => (
                        <div className={styles.figure} key={`${group.context}-${row.currency}`}>
                          <p className={styles.figureValue}>
                            {formatMinorUnits(row.monthlyCents, row.currency)}
                          </p>
                          <p className={styles.figureMeta}>
                            {formatCount(row.activeSubscriptions)} active
                            {row.trialingSubscriptions > 0
                              ? `, ${formatCount(row.trialingSubscriptions)} trialing`
                              : ""}
                          </p>
                        </div>
                      ))}
                    </div>
                    {group.context === "PRINCIPAL" ? (
                      <p className={styles.groupNote}>
                        Gross subscriber spend. BTL keeps its platform fee, recorded per payment.
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className={styles.section} aria-labelledby="revenue-lifecycle">
            <h2 className={styles.sectionTitle} id="revenue-lifecycle">
              New and churned
            </h2>
            {lifecycle.length === 0 ? (
              <p className={styles.empty}>No subscriptions started or ended in this period.</p>
            ) : (
              <div className={styles.groups}>
                {[...lifecycle]
                  .sort((a, b) => contextOrder(a.billingContext) - contextOrder(b.billingContext))
                  .map((row) => (
                    <div className={styles.group} key={row.billingContext}>
                      <p className={styles.groupLabel}>
                        {BILLING_CONTEXT_LABEL[row.billingContext]}
                      </p>
                      <div className={styles.figures}>
                        <div className={styles.figure}>
                          <p className={styles.figureValue}>{formatCount(row.newCount)}</p>
                          <p className={styles.figureMeta}>new</p>
                        </div>
                        <div className={styles.figure}>
                          <p className={styles.figureValue}>{formatCount(row.churnedCount)}</p>
                          <p className={styles.figureMeta}>churned</p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
            <p className={styles.note}>
              Churn counts cancellations. A subscription that lapsed on expiry, or that is still in
              dunning, is not counted until it cancels.
            </p>
          </section>

          <section className={styles.section} aria-labelledby="revenue-tiers">
            <h2 className={styles.sectionTitle} id="revenue-tiers">
              Active subscriptions by tier
            </h2>
            {tiers.length === 0 ? (
              <p className={styles.empty}>No active subscriptions.</p>
            ) : (
              <DataTable
                columns="minmax(180px, 1.4fr) minmax(150px, 1fr) minmax(90px, 0.6fr) minmax(90px, 0.6fr) minmax(80px, 0.5fr) minmax(80px, 0.5fr) minmax(120px, 0.9fr)"
                header={["Tier", "Billing", "Currency", "Cycle", "Active", "Trialing", "Monthly"]}
                headerClassName={styles.tableHeader}
              >
                {tiers.map((tier) => (
                  <DataRow
                    key={`${tier.billingContext}-${tier.tierId}-${tier.currency}-${tier.billingCycle}`}
                  >
                    <span className={styles.strong} data-label="Tier">
                      {tier.tierName || tier.tierId}
                    </span>
                    <span className={styles.meta} data-label="Billing">
                      {BILLING_CONTEXT_LABEL[tier.billingContext]}
                    </span>
                    <span className={styles.meta} data-label="Currency">
                      {tier.currency.toUpperCase()}
                    </span>
                    <span className={styles.meta} data-label="Cycle">
                      {tier.billingCycle}
                    </span>
                    <span className={styles.meta} data-label="Active">
                      {formatCount(tier.activeSubscriptions)}
                    </span>
                    <span className={styles.meta} data-label="Trialing">
                      {formatCount(tier.trialingSubscriptions)}
                    </span>
                    <span className={styles.strong} data-label="Monthly">
                      {formatMinorUnits(tier.monthlyCents, tier.currency)}
                    </span>
                  </DataRow>
                ))}
              </DataTable>
            )}
          </section>

          <section className={styles.section} aria-labelledby="revenue-attribution">
            <h2 className={styles.sectionTitle} id="revenue-attribution">
              Creator and squad attribution
            </h2>
            {principals.length === 0 ? (
              <p className={styles.empty}>No creator or squad subscriptions.</p>
            ) : (
              <DataTable
                columns="minmax(200px, 1.6fr) minmax(100px, 0.7fr) minmax(90px, 0.6fr) minmax(80px, 0.5fr) minmax(80px, 0.5fr) minmax(120px, 0.9fr)"
                header={["Creator or squad", "Type", "Currency", "Active", "Trialing", "Monthly"]}
                headerClassName={styles.tableHeader}
              >
                {principals.map((principal) => (
                  <DataRow key={`${principal.principalId}-${principal.currency}`}>
                    <span className={styles.strong} data-label="Creator or squad">
                      {principal.handle
                        ? `@${principal.handle}`
                        : principal.displayName || principal.principalId}
                    </span>
                    <span className={styles.meta} data-label="Type">
                      {principal.principalType}
                    </span>
                    <span className={styles.meta} data-label="Currency">
                      {principal.currency.toUpperCase()}
                    </span>
                    <span className={styles.meta} data-label="Active">
                      {formatCount(principal.activeSubscriptions)}
                    </span>
                    <span className={styles.meta} data-label="Trialing">
                      {formatCount(principal.trialingSubscriptions)}
                    </span>
                    <span className={styles.strong} data-label="Monthly">
                      {formatMinorUnits(principal.monthlyCents, principal.currency)}
                    </span>
                  </DataRow>
                ))}
              </DataTable>
            )}
            <p className={styles.note}>
              Highest first, top 20. BTL subscriptions are not here: they all belong to the BTL
              account itself.
            </p>
          </section>
        </>
      ) : null}

      {/* Outside the summary conditional on purpose: this section fetches
          Stripe separately and must stay readable when the ledger read
          above fails, and vice versa. */}
      <PayoutsSection />
    </div>
  );
}
